import React, { useState } from "react";
import { Alert, Box, Grid, TextField, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import * as Yup from "yup";
import { Formik } from "formik";
import SnackbarProvider from "app/components/SnackbarProvider";
import { ArrowRightAltOutlined, CheckCircleOutline } from "@mui/icons-material";
import { changePassword } from "app/services/profileServices/Profile";
import { notify } from "app/utils/notifyUtils";

const initialValues = {
  password: "",
  newPassword: "",
  confirmPassword: "",
};

interface FormValues {
  password: string;
  newPassword: string;
  confirmPassword: string;
}

const validationSchema = Yup.object().shape({
  password: Yup.string().required("Senha é um campo obrigatório"),
  newPassword: Yup.string()
    .min(6, "A senha deve ter no mínimo 6 caracteres")
    .required("Nova Senha é um campo obrigatório"),
  confirmPassword: Yup.string()
    .required("Confirmar Senha é um campo obrigatório")
    .oneOf([Yup.ref("newPassword")], "As senhas devem coincidir"),
});

const FormChangePassword = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const snackbarProvider = SnackbarProvider();

  const handleFormSubmit = async (values: FormValues) => {
    setLoading(true);

    try {
      await changePassword(
        values.password,
        values.newPassword,
        values.confirmPassword
      );
      notify("success", "Senha alterada com sucesso");
      setSuccess(true);
    } catch (e: any) {
      notify("error", e.response.data.response);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Alert
        icon={<CheckCircleOutline fontSize="inherit" />}
        severity="success"
      >
        Senha alterada com sucesso
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        p: 3,
        width: "100%",
        mx: "auto",
      }}
    >
      <Typography
        variant="h5"
        gutterBottom
        sx={{
          marginBottom: 1,
          fontWeight: 600,
          color: "#1a237e",
          letterSpacing: "-0.5px",
        }}
      >
        Alterar Senha
      </Typography>

      <Typography variant="body1" sx={{ marginBottom: 3, color: "#000" }}>
        Preencha os campos com as informações de sua Instituição
      </Typography>

      <Formik
        onSubmit={handleFormSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
        }) => (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2} my={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Senha"
                  name="password"
                  placeholder="Digite sua senha"
                  size="medium"
                  type="password"
                  variant="outlined"
                  onBlur={handleBlur}
                  value={values.password}
                  onChange={handleChange}
                  helperText={touched.password && errors.password}
                  error={Boolean(errors.password && touched.password)}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} my={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nova Senha"
                  name="newPassword"
                  placeholder="Digite sua nova senha"
                  size="medium"
                  type="password"
                  variant="outlined"
                  onBlur={handleBlur}
                  value={values.newPassword}
                  onChange={handleChange}
                  helperText={touched.newPassword && errors.newPassword}
                  error={Boolean(errors.newPassword && touched.newPassword)}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} my={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Confirmar Senha"
                  name="confirmPassword"
                  placeholder="Confirme sua nova senha"
                  size="medium"
                  type="password"
                  variant="outlined"
                  onBlur={handleBlur}
                  value={values.confirmPassword}
                  onChange={handleChange}
                  helperText={touched.confirmPassword && errors.confirmPassword}
                  error={Boolean(
                    errors.confirmPassword && touched.confirmPassword
                  )}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} my={2}>
              <Grid item xs={6} sm={6}>
                <LoadingButton
                  type="submit"
                  color="primary"
                  loading={loading}
                  variant="contained"
                  endIcon={<ArrowRightAltOutlined fontSize="small" />}
                >
                  Salvar
                </LoadingButton>
              </Grid>
            </Grid>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default FormChangePassword;
